/**
 * Zod schema for the `audiences` collection (ADR-120).
 *
 * The audience primitive represents "who can see this" for any targetable
 * doc (sessions, travelItems, notifications). Two boolean flags classify
 * four faces of one primitive:
 *
 *   |              | name != null               | name == null              |
 *   |--------------|----------------------------|---------------------------|
 *   | system:false | Custom group ("Skiers")    | Anonymous composition     |
 *   | system:true  | Role audience              | Personal audience         |
 *   |              | ("All Invitees")           | (one per user per event)  |
 *
 * - **Custom groups**: organizer-managed on the Groups page. Editable,
 *   deletable. Reusable across docs.
 * - **Role audiences**: bootstrapped per event ("All Members", "All
 *   Invitees", "All Companions", "All Internal Team", "All Organizers",
 *   "All Admins"). Immutable from callables; deleted with the event.
 * - **Anonymous compositions**: created by a parent compose form when the
 *   organizer mixes multiple audiences or audiences + people. Hidden from
 *   the picker. Edited only via the parent form. Deleted with the parent.
 * - **Personal audiences**: one per person/event targeting handle. Pending
 *   contacts get one when they are invited to the event, before they have
 *   an Auth UID; activateAccount fills in `members.userIds[]` when they
 *   claim the contact. Existing users also get one when added to an event.
 *
 * Resolution: each audience's `resolvedPersonIds[]` is the flat union of
 *   - role-based members (eventParticipants.where(eventId, role in roles))
 *   - direct members (members.personIds)
 *   - composed members (members.includedAudienceIds → recurse)
 * with depth-limit 5 and cycle detection. Cycles and over-depth are
 * rejected at write time on the callables; the resolver's check is a
 * defensive backstop.
 *
 * Consumers (sessions, travelItems, notifications) reference one audience
 * via `audienceId: string`. Their CF-managed `targetedPersonIds[]` is
 * copied directly from the linked audience's `resolvedPersonIds[]`
 * (ADR-137). Security rules match against `request.auth.token.personId`,
 * so the cache is personId-keyed end-to-end. Pending people are present
 * from the moment they're added to the audience.
 */

import { z } from 'zod';
import { roleSchema } from './role';

export const audienceMembersSchema = z.object({
  /** Include everyone with one of these per-event roles. */
  roles: z.array(roleSchema),
  /**
   * Plus these specific persons (direct adds, personId-keyed). ADR-136
   * Phase 2: personIds accept pending + claimed members uniformly.
   * Resolver filters via `eventParticipants/{eventId}_{personId}` and
   * feeds the personId-keyed `resolvedPersonIds[]` cache directly
   * (ADR-137 dropped the uid projection step). Soft cap of 500 to fail
   * fast on typo'd or runaway pastes.
   */
  personIds: z.array(z.string()).max(500),
  /**
   * Plus everyone resolved by these other audiences (composition).
   * Resolved via depth-limited DFS with cycle detection. Depth limit: 5.
   * Cycles and depth violations are rejected at write time on the
   * callable; the resolver's check is a defensive backstop.
   */
  includedAudienceIds: z.array(z.string()),
});

export const audienceDocumentSchema = z.object({
  eventId: z.string(),
  /**
   * Display name. Null = anonymous; hidden from the Audience picker and
   * the Groups tab. Used for compositions (created by a parent compose
   * form) and personal audiences (one per user per event).
   */
  name: z.string().nullable(),
  description: z.string().nullable(),

  /** Who is in this audience. See `audienceMembersSchema`. */
  members: audienceMembersSchema,

  /**
   * Flat list of personIds derived from `members` by `onAudienceWritten`.
   * CF-managed; clients cannot write this. Aggregator triggers on
   * consumers (sessions/travel/notifications) copy this into their own
   * `targetedPersonIds[]`.
   *
   * ADR-137: includes pending + claimed members uniformly. The earlier
   * `resolvedUserIds[]` uid projection was dropped — rules now match on
   * `request.auth.token.personId`, push delivery resolves uid via
   * `persons[personId].authUid` at fan-out time, and email delivery
   * resolves email via `personProfiles[personId].email`.
   */
  resolvedPersonIds: z.array(z.string()),

  /**
   * True for auto-bootstrapped audiences (role audiences and personal
   * audiences). Immutable from the callable layer — `updateAudience`
   * and `deleteAudience` reject targets where this is true. Only the
   * bootstrap paths (`onEventCreatedBootstrapAudiences` for role audiences,
   * invitation/activation paths for personal audiences) write `system: true`.
   */
  system: z.boolean(),
});

/**
 * Maximum recursion depth for `members.includedAudienceIds`. The
 * audience callables reject writes that would exceed this depth, and
 * the resolver enforces it as a defensive backstop.
 */
export const AUDIENCE_COMPOSITION_DEPTH_LIMIT = 5;

/**
 * Stable ID format for personal audiences. The second segment is the stable
 * person handle available at creation time: contact id before claim, Auth UID
 * for already-authenticated users and legacy memberships.
 */
export function personalAudienceId(eventId: string, personId: string): string {
  return `aud_personal_${eventId}_${personId}`;
}
